'use client';

import { useEffect, useState } from 'react';
import { Plus, X, ThumbsDown, Check, Play } from 'lucide-react';

interface MobileContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  isInWatchlist: boolean;
  isDisliked: boolean;
  isWatched?: boolean;
  mediaType: 'tv' | 'movie';
  onAddToWatchlist: () => void;
  onRemoveFromWatchlist: () => void;
  onDislike: () => void;
  onMarkWatched?: () => void;
  onPlay: () => void;
}

export function MobileContextMenu({
  isOpen,
  onClose,
  title,
  isInWatchlist,
  isDisliked,
  isWatched,
  mediaType,
  onAddToWatchlist,
  onRemoveFromWatchlist,
  onDislike,
  onMarkWatched,
  onPlay
}: MobileContextMenuProps) {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      setDragOffset(0); // Reset drag offset when menu closes
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  // Drag handlers for dismissing the menu
  const handleDragStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
  };

  const handleDragMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const offset = Math.max(0, currentY - dragStartY);
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    // Close menu if dragged down more than 100px
    if (dragOffset > 100) {
      onClose();
    }
    // Reset offset
    setDragOffset(0);
    setDragStartY(0);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/85 z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Menu */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-300"
        style={{
          transform: `translateY(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        <div className="bg-gray-900/95 backdrop-blur-xl rounded-t-3xl shadow-[0_-10px_80px_rgba(220,38,38,0.3)]">
          {/* Handle bar - Draggable area */}
          <div
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="w-12 h-1.5 bg-gray-700 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-start justify-between mb-6 pb-4 px-6">
            <div className="flex-1 pr-4">
              <h3 className="text-lg font-bold text-white line-clamp-2">
                {title}
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                {mediaType === 'tv' ? 'TV Show' : 'Movie'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-red-600/20 transition-all text-gray-400 hover:text-red-400 flex-shrink-0"
              style={{minWidth: '40px', minHeight: '40px', maxWidth: '40px', maxHeight: '40px'}}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Actions */}
          <div className="space-y-3 px-6 pb-6">
            {/* Play/View Details Action */}
            <button
              onClick={() => handleAction(onPlay)}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-red-600/30 to-red-600/10 border-2 border-red-600/50 hover:border-red-600 transition-all group"
            >
              <div
                className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg"
                style={{minWidth: '48px', minHeight: '48px', maxWidth: '48px', maxHeight: '48px'}}
              >
                <Play className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-semibold text-lg">View Details</p>
                <p className="text-sm text-gray-400">See full information</p>
              </div>
            </button>

            {/* Watchlist Action */}
            {!isInWatchlist ? (
              <button
                onClick={() => handleAction(onAddToWatchlist)}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-red-600/20 to-red-600/5 border border-red-600/30 hover:border-red-600/50 transition-all group"
              >
                <div
                  className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform"
                  style={{minWidth: '48px', minHeight: '48px', maxWidth: '48px', maxHeight: '48px'}}
                >
                  <Plus className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-semibold">Add to Watchlist</p>
                  <p className="text-sm text-gray-400">Save to watch later</p>
                </div>
              </button>
            ) : (
              <button
                onClick={() => handleAction(onRemoveFromWatchlist)}
                className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-gray-800/50 to-gray-800/20 border border-gray-700 hover:border-red-600/50 transition-all group"
              >
                <div
                  className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center group-hover:scale-110 group-hover:bg-red-600 transition-all"
                  style={{minWidth: '48px', minHeight: '48px', maxWidth: '48px', maxHeight: '48px'}}
                >
                  <X className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-semibold">Remove from Watchlist</p>
                  <p className="text-sm text-gray-400">Already in your list</p>
                </div>
              </button>
            )}

            {/* Dislike Action */}
            <button
              onClick={() => handleAction(onDislike)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all group ${
                isDisliked
                  ? 'bg-gradient-to-r from-orange-600/20 to-orange-600/5 border-orange-600/30 hover:border-orange-600/50'
                  : 'bg-gradient-to-r from-gray-800/50 to-gray-800/20 border-gray-700 hover:border-orange-600/50'
              }`}
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-110 transition-all ${
                  isDisliked ? 'bg-orange-600' : 'bg-gray-700 group-hover:bg-orange-600'
                }`}
                style={{minWidth: '48px', minHeight: '48px', maxWidth: '48px', maxHeight: '48px'}}
              >
                <ThumbsDown className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-semibold">
                  {isDisliked ? 'Remove Dislike' : 'Not Interested'}
                </p>
                <p className="text-sm text-gray-400">
                  {isDisliked ? 'You marked this as not interested' : 'Hide similar recommendations'}
                </p>
              </div>
            </button>

            {/* Mark as Watched (Movies Only) */}
            {mediaType === 'movie' && onMarkWatched && (
              <button
                onClick={() => handleAction(onMarkWatched)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all group ${
                  isWatched
                    ? 'bg-gradient-to-r from-green-600/20 to-green-600/5 border-green-600/30 hover:border-green-600/50'
                    : 'bg-gradient-to-r from-gray-800/50 to-gray-800/20 border-gray-700 hover:border-green-600/50'
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-110 transition-all ${
                    isWatched ? 'bg-green-600' : 'bg-gray-700 group-hover:bg-green-600'
                  }`}
                  style={{minWidth: '48px', minHeight: '48px', maxWidth: '48px', maxHeight: '48px'}}
                >
                  <Check className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-white font-semibold">
                    {isWatched ? 'Mark as Unwatched' : 'Mark as Watched'}
                  </p>
                  <p className="text-sm text-gray-400">
                    {isWatched ? 'You already watched this' : 'Track your viewing history'}
                  </p>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
